﻿import { Download, FileSpreadsheet, PlusCircle, Upload } from "lucide-react";
import { formatNumber } from "@/utils/numberFormat";

import ListScaffold from "@/components/desktop/list/table/ListScaffold";
import CustomConfirmModal from "@/components/desktop/modal/CustomConfirmModal";
import CustomProgressIndicator from "@/components/desktop/indicators/CustomProgressIndicator";
import CustomInfoMessage from "@/components/desktop/list/CustomInfoMessage";
import CustomFetching from "@/components/desktop/fetching/CustomFetching";
import CustomNoPermissionView from "@/components/desktop/permission/no-permission/CustomNoPermissionView";
import PhytosanitaryProgramOAs from "./PhytosanitaryProgramOAs";
import PhytosanitaryProgressModal from "./PhytosanitaryProgressModal";
import PhytosanitaryApplicationForm from "./components/ApplicationForm/PhytosanitaryApplicationForm";

import usePhytosanitaryProgramController from "./hooks/ProgramData/usePhytosanitaryProgramController";
import { ROWSPAN_CONFIG } from "./phytosanitary_program.constants";

const PhytosanitaryProgram = () => {
	const {
		navigate,
		inputRefExcel,
		lookups,
		isLoading,
		isFetching,
		isLoadingPermissions,
		canShow,
		isLoadingLookups,
		isLoadingSave,
		isLoadingCreateOA,
		isLoadingImport,
		isLoadingDeleteApplication,
		isLoadingAddRows,
		data,
		transformedData,
		columns,
		tableState,
		reactTableSort,
		filtersState,
		isFilterOpen,
		infoMessages,
		messageType,
		clearMessages,
		openFilter,
		closeFilter,
		handleApplyFilters,
		handleClearAllFilters,
		handleClearOneFilter,
		handlePageChange,
		handlePageSizeChange,
		handleSortByChange,
		handleFilterChange,
		activeSpeciesId,
		handleExport,
		totalProgress,
		totalSurface,
		isProgressModalOpen,
		setProgressModalOpen,
		progressData,
		showConfirmModal,
		validationData,
		handleImportClick,
		handleFileChange,
		handleConfirmImport,
		handleCancelImport,
		isConfirmModalOpen,
		productToRemove,
		confirmRemoveProduct,
		cancelRemoveProduct,
		applicationToDelete,
		confirmDeleteApplication,
		cancelDeleteApplication,
		applicationFormMode,
		applicationFormRow,
		applicationFormRows,
		isIrrigation,
		handleNewApplication,
		closeApplicationForm,
		handleSubmitApplicationForm,
		applicationForm,
	} = usePhytosanitaryProgramController();

	const renderExpandedContent = (row) => <PhytosanitaryProgramOAs OAs={row.original.application_orders || []} />;

	if (isLoadingPermissions) {
		return <CustomFetching center message="Validando permisos..." />;
	}

	if (!canShow) {
		return <CustomNoPermissionView />;
	}

	return (
		<ListScaffold
			titleBreadcrumbs={[
				{ label: "Dashboard", onClick: () => navigate("/") },
				{ label: "Planificación", onClick: () => navigate("/planificacion") },
				{ label: "Programa Fitosanitario", onClick: () => navigate("/planificacion/programa_fitosanitario") },
			]}
			subtitle="Gestiona tus programas fitosanitarios y genera órdenes de aplicación."
			topContent={
				infoMessages.length > 0 ? (
					<CustomInfoMessage messages={infoMessages} type={messageType} autoCloseDuration={30000} onClose={clearMessages} />
				) : null
			}
			rightExtra={
				<div style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem" }}>
					<div style={{ display: "flex", flexDirection: "column", gap: "0.125rem", padding: "0 0.75rem", minWidth: "160px" }}>
						<span style={{ fontSize: "0.7rem", fontWeight: 500, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.025em" }}>
							Superficie Total
						</span>
						<span style={{ fontSize: "1.25rem", fontWeight: 700, color: "#111827", lineHeight: 1 }}>{formatNumber(totalSurface)} Ha</span>
					</div>
					<div style={{ width: "1px", height: "40px", background: "#e5e7eb" }} />
					<CustomProgressIndicator
						label="Progreso"
						percentage={totalProgress}
						variant="primary"
						size="md"
						onClick={() => setProgressModalOpen(true)}
					/>
				</div>
			}
			actionButtons={[
				{
					key: "actions",
					label: "Acciones",
					icon: FileSpreadsheet,
					options: [
						{
							key: "export",
							label: "Exportar Programa",
							icon: Download,
							onClick: () => handleExport(activeSpeciesId),
						},
						{
							key: "import",
							label: "Cargar Nuevo Programa",
							icon: Upload,
							onClick: handleImportClick,
						},
						{
							key: "new_application",
							label: "Nueva Aplicación",
							icon: PlusCircle,
							onClick: handleNewApplication,
						},
					],
				},
			]}
			filterTitle="Filtrar Programa Fitosanitario"
			isFilterOpen={isFilterOpen}
			filters={filtersState}
			onFilterChange={handleFilterChange}
			onApplyFilters={handleApplyFilters}
			onClearFilters={handleClearAllFilters}
			onClearOneFilter={handleClearOneFilter}
			onOpenFilter={openFilter}
			onCloseFilter={closeFilter}
			isFetching={isFetching}
			isLoading={isLoading || isLoadingImport || isLoadingLookups || isLoadingSave || isLoadingCreateOA || isLoadingDeleteApplication || isLoadingAddRows}
			data={transformedData}
			totalItems={data?.total || 0}
			totalPages={data?.last_page || 0}
			toItem={data?.to || 0}
			fromItem={data?.from || 0}
			pageIndex={tableState.page}
			columns={columns}
			onPageChange={handlePageChange}
			pageSize={tableState.per_page}
			onPageSizeChange={handlePageSizeChange}
			sortBy={reactTableSort}
			onSortByChange={handleSortByChange}
			pageSizeOptions={[20, 50, 100]}
			initialPageSize={tableState.per_page}
			initialSortBy={reactTableSort}
			compactHeaders={true}
			compactBody={true}
			useExpandableRows={true}
			renderExpandedContent={renderExpandedContent}
			useRowspan={true}
			rowspanConfig={ROWSPAN_CONFIG}
		>
			<input ref={inputRefExcel} type="file" accept=".xlsx, .xls" style={{ display: "none" }} onChange={handleFileChange} />

			<CustomConfirmModal
				open={showConfirmModal}
				onClose={handleCancelImport}
				onConfirm={handleConfirmImport}
				title="Programa Existente"
				message={validationData?.message || "¿Deseas continuar?"}
				confirmText="Sí, continuar"
				cancelText="No, cancelar"
				isLoading={isLoadingImport}
			/>

			<CustomConfirmModal
				open={isConfirmModalOpen}
				onClose={cancelRemoveProduct}
				onConfirm={confirmRemoveProduct}
				title="Confirmar eliminación"
				message={`¿Está seguro de que desea quitar el producto ${productToRemove?.product_text}?`}
				confirmText="Sí, eliminar"
				cancelText="No, cancelar"
			/>

			<CustomConfirmModal
				open={Boolean(applicationToDelete)}
				onClose={cancelDeleteApplication}
				onConfirm={confirmDeleteApplication}
				title="Eliminar aplicación"
				message={`¿Seguro que desea eliminar la aplicación N°${applicationToDelete?.application_number} del programa ${applicationToDelete?.name}? Esta acción no se puede deshacer.`}
				confirmText="Sí, eliminar"
				cancelText="No, cancelar"
				isLoading={isLoadingDeleteApplication}
				verificationText="eliminar"
			/>

			<PhytosanitaryProgressModal open={isProgressModalOpen} onClose={() => setProgressModalOpen(false)} progressData={progressData} />

			<PhytosanitaryApplicationForm
				open={Boolean(applicationFormMode)}
				onClose={closeApplicationForm}
				lookups={lookups}
				mode={applicationFormMode}
				isIrrigation={isIrrigation}
				applicationFormRow={applicationFormRow}
				applicationFormRows={applicationFormRows}
				formState={applicationForm.formState}
				formErrors={applicationForm.formErrors}
				updateHeaderField={applicationForm.updateHeaderField}
				addGroup={applicationForm.addGroup}
				removeGroup={applicationForm.removeGroup}
				addProductToGroup={applicationForm.addProductToGroup}
				removeProductFromGroup={applicationForm.removeProductFromGroup}
				addAlternativeToGroup={applicationForm.addAlternativeToGroup}
				removeAlternativeFromGroup={applicationForm.removeAlternativeFromGroup}
				updateProduct={applicationForm.updateProduct}
				updateAlternative={applicationForm.updateAlternative}
				onSubmit={handleSubmitApplicationForm}
				isLoading={isLoadingAddRows}
			/>
		</ListScaffold>
	);
};

export default PhytosanitaryProgram;
